import { useMemo } from "react";
import { uniqBy } from "lodash";
import buffer from "@turf/buffer";
import { Title, TitleWithOwnerNames } from "react-migration/domains/ownership/typings/Title";
import {
  OwnershipIntersectableGeometry,
  useTitlesByLocationQuery,
} from "react-migration/domains/ownership/apollo/useTitlesByLocationQuery";
import { Nullable } from "src/js/types/Nullable";
import { isDefined } from "react-migration/lib/util/isDefined";
import { canonicalizeOwnerNames } from "../OwnershipSelectionDetailView/canonicalizeOwnerName";
import { SelectionGeometry } from "src/js/stores/map/store";

export function coerceToOwnershipIntersectableGeometry(
  geometry: Nullable<SelectionGeometry>
): Nullable<OwnershipIntersectableGeometry> {
  if (geometry?.type !== "MultiLineString" && geometry?.type !== "LineString") return geometry;
  return buffer(geometry, 1, { units: "meters", steps: 1 }).geometry;
}

export function enrichTitleWithOwnerNames(title: Title): TitleWithOwnerNames {
  return { ...title, ownerNames: canonicalizeOwnerNames(title.owners) };
}

type UseTitlesWithOwnerNamesQueryOptions = {
  skip?: boolean;
};

export function useTitlesWithOwnerNamesQuery(
  location?: Nullable<OwnershipIntersectableGeometry>,
  options?: UseTitlesWithOwnerNamesQueryOptions
) {
  const { titlesByLocation, loading } = useTitlesByLocationQuery({
    location,
    limit: 250,
    skip: options?.skip,
  });

  const titlesWithOwnerNames = useMemo(
    () =>
      uniqBy([...titlesByLocation], (title) => title?.title_no)
        .filter(isDefined)
        .map(enrichTitleWithOwnerNames),
    [titlesByLocation]
  );

  return {
    titlesWithOwnerNames,
    loading,
  };
}
